import { computed } from "vue";
import authService from "../services/AuthService";
import { hasCapability } from "./useCapabilities";

/**
 * Admin groups — each admin user belongs to one or more Okta groups.
 * Groups are kept here for DISPLAY only (the role label). What the UI may show
 * is decided by capabilities (issue #1878), fetched from /admin/me/capabilities
 * and checked via hasCapability — never derived from groups client-side.
 *
 * Okta group names follow the pattern: pam_{env}_{group}
 * e.g. pam_local_ops_cs, pam_qa_super_admin, pam_prod_crm
 */
export const ADMIN_GROUPS = {
    SUPER_ADMIN: "super_admin",
    OPS_CS: "ops_cs",
    OPS_RP: "ops_rp",
    CASINO_OPS: "casino_ops",
    AFFILIATE_MGR: "affiliate_mgr",
    CRM: "crm",
    PII_VIEWER: "pii_viewer"
};

/**
 * Display labels for groups
 */
export const GROUP_LABELS = {
    [ADMIN_GROUPS.SUPER_ADMIN]: "Super Admin",
    [ADMIN_GROUPS.OPS_CS]: "Ops Admin - Customer Support",
    [ADMIN_GROUPS.OPS_RP]: "Ops Admin - Risk & Payments",
    [ADMIN_GROUPS.CASINO_OPS]: "Admin - Casino Operations",
    [ADMIN_GROUPS.AFFILIATE_MGR]: "Admin - Affiliate Manager",
    [ADMIN_GROUPS.CRM]: "Admin - CRM",
    [ADMIN_GROUPS.PII_VIEWER]: "PII Viewer"
};

/**
 * PII access level per group — DISPLAY ONLY (the Groups & Permissions screen).
 * Real PII gating uses the pii.view / pii.edit capabilities.
 */
export const GROUP_PII_ACCESS = {
    [ADMIN_GROUPS.SUPER_ADMIN]: "read_write",
    [ADMIN_GROUPS.OPS_CS]: "read_write",
    [ADMIN_GROUPS.OPS_RP]: "read_write",
    [ADMIN_GROUPS.CASINO_OPS]: "read_only",
    [ADMIN_GROUPS.AFFILIATE_MGR]: "read_only",
    [ADMIN_GROUPS.CRM]: "read_only",
    [ADMIN_GROUPS.PII_VIEWER]: "read_only"
};

/**
 * Group -> capabilities, DISPLAY ONLY, for the read-only Groups & Permissions
 * screen (#1595). This MIRRORS the authoritative backend matrix in
 * internal/rbac/capabilities.go — it is NOT used for any access decision (those
 * go through /admin/me/capabilities), so drift here only mis-labels that one
 * informational view. super_admin is omitted (it holds every capability).
 * TODO(#1878): serve this from the backend so the mirror can be deleted.
 */
export const GROUP_CAPABILITIES = {
    [ADMIN_GROUPS.OPS_CS]: [
        "main.users", "main.deposits", "main.credit_purchases", "main.withdrawals",
        "main.verifications", "main.anti_fraud", "admin.bulk_users", "pii.view", "pii.edit"
    ],
    [ADMIN_GROUPS.OPS_RP]: [
        "main.users", "main.deposits", "main.credit_purchases", "main.withdrawals",
        "main.verifications", "main.anti_fraud",
        "site.security_banned_ips", "site.security_banned_devices", "site.security_banned_domains",
        "admin.bulk_users", "pii.view", "pii.edit"
    ],
    [ADMIN_GROUPS.CASINO_OPS]: [
        "main.users", "game.games", "game.catalogs", "game.categories",
        "game.placement_editor", "pii.view"
    ],
    [ADMIN_GROUPS.AFFILIATE_MGR]: [
        "main.users", "main.affiliates", "site.postbacks", "pii.view"
    ],
    [ADMIN_GROUPS.CRM]: [
        "main.users", "content.popups", "content.carousel", "content.player_targeting",
        "content.promotions", "content.credit_packages_store",
        "content.credit_packages_purchase_offers", "content.credit_packages_amwest",
        "content.promo_credit_templates", "content.quest_system", "content.daily_bonus",
        "content.sitewide_notifications", "site.customerio", "pii.view"
    ],
    [ADMIN_GROUPS.PII_VIEWER]: ["pii.view"]
};

/**
 * Group priority — most privileged first — used only to pick a single display
 * label when an admin is in several groups. NOT used for access decisions.
 */
const GROUP_PRIORITY = [
    ADMIN_GROUPS.SUPER_ADMIN,
    ADMIN_GROUPS.OPS_RP,
    ADMIN_GROUPS.OPS_CS,
    ADMIN_GROUPS.CASINO_OPS,
    ADMIN_GROUPS.AFFILIATE_MGR,
    ADMIN_GROUPS.CRM,
    ADMIN_GROUPS.PII_VIEWER
];

/**
 * RBAC capabilities (mirror of internal/rbac/capabilities.go constant strings).
 */
export const CAPABILITIES = {
    PII_VIEW: "pii.view",
    PII_EDIT: "pii.edit"
};

/**
 * Route prefix -> capability. The single mapping that drives both menu item
 * visibility and the router guard. A route NOT listed here has no capability
 * and is treated as super_admin-only (see routeAllowed / isItemAllowed), which
 * matches the backend matrix where unlisted sections are super-only.
 *
 * Keep route keys longest-first-friendly: capabilityForPath matches the longest
 * prefix, so "/security/banned-ips" wins over a hypothetical "/security".
 */
export const ROUTE_CAPABILITIES = {
    "/users": "main.users",
    "/affiliates": "main.affiliates",
    "/deposits": "main.deposits",
    "/credit-purchases": "main.credit_purchases",
    "/withdrawals": "main.withdrawals",
    "/payout-review": "main.withdrawals",
    // Sweeps transaction queues (#2158) — mirror the real-money analogues so the
    // same ops/CS roles (main.deposits/withdrawals) see them, not super-admin only.
    // Purchases (buy coins) ~ deposits; AMOE (free entry) is a purchase-adjacent
    // ops queue; Redemptions (cash out) ~ withdrawals.
    "/purchases": "main.deposits",
    "/amoe": "main.deposits",
    "/redemptions": "main.withdrawals",
    "/verifications": "main.verifications",
    "/anti-fraud": "main.anti_fraud",
    "/desync-queue": "main.desync_queue",

    "/popups": "content.popups",
    "/carousels": "content.carousel",
    "/player-targeting": "content.player_targeting",
    "/promotions": "content.promotions",
    "/store-packages": "content.credit_packages_store",
    "/purchase-offers": "content.credit_packages_purchase_offers",
    "/amwest-credit-packs": "content.credit_packages_amwest",
    "/promo-credit-templates": "content.promo_credit_templates",
    "/quests": "content.quest_system",
    "/daily-bonus": "content.daily_bonus",
    "/sitewide-notifications": "content.sitewide_notifications",

    "/games": "game.games",
    "/game-catalogs": "game.catalogs",
    "/game-categories": "game.categories",
    "/game-placement-editor": "game.placement_editor",
    "/all-games-order": "game.placement_editor",

    "/global-configurations": "site.global_configurations",
    "/security/banned-ips": "site.security_banned_ips",
    "/security/banned-devices": "site.security_banned_devices",
    "/security/banned-domains": "site.security_banned_domains",
    "/loyalty-levels": "site.loyalty_levels",
    "/refer-a-friend": "site.refer_a_friend",
    "/game-providers": "site.game_providers",
    "/payment-providers": "site.payment_providers",
    "/xtremepush": "site.customerio",
    "/postbacks": "site.postbacks",
    "/deploy-dashboard": "site.deploy_dashboard",

    "/admin/maintenance": "admin.maintenance_mode",
    "/admin/feature-flags": "admin.feature_flags",
    "/admin/bulk-users": "admin.bulk_users"
};

// Routes always available to any authenticated admin, regardless of capability.
// /select-brand is a per-admin preference page (like /settings): the backend
// PUT /admin/me/brand has no capability gate, so the picker shouldn't either (#2122).
const UNIVERSAL_ROUTES = ["/", "/settings", "/select-brand"];

/**
 * Strip environment prefix from an Okta group name to get the base group.
 * e.g. "pam_local_ops_cs" -> "ops_cs", "pam_prod_super_admin" -> "super_admin"
 */
function stripEnvPrefix(group) {
    const match = group.match(/^pam_(?:local|qa|staging|prod)_(.+)$/);
    return match ? match[1] : group;
}

/**
 * Whether the current admin belongs to the super_admin group. Used only to gate
 * routes/menu items that have no capability mapping (super_admin-only sections).
 */
function isSuperAdminFromGroups() {
    return authService
        .getGroups()
        .some(group => stripEnvPrefix(group) === ADMIN_GROUPS.SUPER_ADMIN);
}

/**
 * Resolve a single display group (highest priority) for the current admin.
 */
function resolveDisplayGroup() {
    const resolved = new Set(
        authService.getGroups().map(stripEnvPrefix).filter(base => GROUP_PRIORITY.includes(base))
    );
    return GROUP_PRIORITY.find(group => resolved.has(group)) ?? null;
}

/**
 * Find the capability governing a route path via longest-prefix match.
 * Returns null when no capability maps to the path.
 */
function capabilityForPath(path) {
    let best = null;
    for (const prefix of Object.keys(ROUTE_CAPABILITIES)) {
        if (path === prefix || path.startsWith(prefix + "/")) {
            if (!best || prefix.length > best.length) {
                best = prefix;
            }
        }
    }
    return best ? ROUTE_CAPABILITIES[best] : null;
}

function isUniversalRoute(path) {
    return UNIVERSAL_ROUTES.some(r => path === r || path.startsWith(r + "/"));
}

/**
 * Standalone route-access check (for the router guard, outside component context).
 * Universal routes are always allowed; capability-mapped routes require the
 * capability; everything else is super_admin-only.
 */
export function routeAllowed(path) {
    if (isUniversalRoute(path)) return true;
    const capability = capabilityForPath(path);
    if (capability) return hasCapability(capability);
    return isSuperAdminFromGroups();
}

/**
 * Composable for capability-based access control.
 */
export function useRoles() {
    const currentGroup = computed(() => resolveDisplayGroup());

    const currentGroupLabel = computed(() => GROUP_LABELS[currentGroup.value] || "No Role");

    const isSuperAdmin = computed(() => isSuperAdminFromGroups());

    const canViewPII = computed(() => hasCapability(CAPABILITIES.PII_VIEW));
    const canEditPII = computed(() => hasCapability(CAPABILITIES.PII_EDIT));

    /**
     * Whether a specific menu item should be shown. Items with sub-items are
     * shown when any child is; the dashboard and other universal routes are
     * always shown; capability-mapped routes require the capability; anything
     * else is super_admin-only.
     */
    function isItemAllowed(item) {
        if (item.items) {
            return item.items.some(subItem => isItemAllowed(subItem));
        }
        if (!item.to) return false;
        if (isUniversalRoute(item.to)) return true;

        const capability = capabilityForPath(item.to);
        if (capability) return hasCapability(capability);
        return isSuperAdmin.value;
    }

    /**
     * Whether a menu section should be shown — true if any of its items is.
     */
    function isSectionAllowed(_sectionLabel, sectionItems) {
        if (!sectionItems) return isSuperAdmin.value;
        return sectionItems.some(item => isItemAllowed(item));
    }

    /**
     * Route-access check for the current user (composable form).
     */
    function hasRouteAccess(path) {
        return routeAllowed(path);
    }

    return {
        currentGroup,
        currentGroupLabel,
        isSuperAdmin,
        canViewPII,
        canEditPII,
        isSectionAllowed,
        isItemAllowed,
        hasRouteAccess,
        ADMIN_GROUPS,
        GROUP_LABELS,
        CAPABILITIES
    };
}

// Standalone exports for use outside component context (e.g. router guard).
export { routeAllowed as isRouteAllowed, stripEnvPrefix };

export default useRoles;
